import { Component, OnInit, Input, AfterViewInit, ViewChild } from '@angular/core';
import {
  LeaderboardV2Query,
  LeaderboardV2Service,
  MonthlyLeaderboardEntry,
  LeaderboardV2Entry,
  LeaderboardType,
  Sort,
} from '@fgb/core';
import { OwlCarouselSettings, sortMultiple } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { CarouselComponent, OwlOptions } from 'ngx-owl-carousel-o';
import { firstValueFrom } from 'rxjs';

interface LeaderboardEntryWithTier extends LeaderboardV2Entry {
  tier?: string;
}

@Component({
  selector: 'fgb-leaderboard',
  templateUrl: './leaderboard.component.html',
  styleUrls: ['./leaderboard.component.scss'],
})
export class LeaderboardComponent implements OnInit, AfterViewInit {
  @Input() toShow: number;
  @Input() LeaderboardType: LeaderboardType;
  @Input() isWidget: boolean = false;
  type = LeaderboardType;

  @ViewChild('owlCarouselRef') owlCarouselRef: CarouselComponent;

  yearlyLeaderboards: LeaderboardV2Entry[] | undefined;
  monthly: MonthlyLeaderboardEntry;
  monthlyLeaderboard: any[] = [];
  showMonthly: boolean = true;
  loadingMonth: number | null = null;
  loadingYear: boolean = true;
  currentUser: LeaderboardV2Entry | undefined;
  topThree: LeaderboardEntryWithTier[] = [];
  challengers: LeaderboardV2Entry[] = [];
  customOptions: OwlOptions = {
    items: 1,
    margin: OwlCarouselSettings.Margin,
    mouseDrag: false,
    touchDrag: false,
    pullDrag: false,
    navSpeed: 700,
    nav: true,
    dots: false,
    startPosition: this.monthlyLeaderboard.length - 1,
    navText: ['<i class="material-icons fw-bold">chevron_left</i>', '<i class="material-icons fw-bold">chevron_right</i>'],
    stagePadding: 0,
  };
  isUserTopThree: boolean = false;

  constructor(private leaderboardQuery: LeaderboardV2Query, private leaderboardService: LeaderboardV2Service) {}

  ngOnInit() {
    this.initializeMonthlyLeaderboard();
  }

  ngAfterViewInit() {
    this.reinitializeOwlCarousel();
  }

  initializeLeaderboard() {
    this.showMonthly = false;
    if (!this.yearlyLeaderboards) {
      this.loadingYear = true;
    } else {
      this.loadingYear = false;
    }
    this.currentUser = undefined;
    firstValueFrom(this.leaderboardService.fetchLeaderboardV2(this.toShow, this.LeaderboardType))
      .then(() => {
        this.currentUser = this.leaderboardQuery.getLeaderboardV2().find((entry) => entry.IsCurrentUser);
        this.yearlyLeaderboards = this.leaderboardQuery
          .getLeaderboardV2()
          .sort(sortMultiple(Sort.by('Position'), Sort.by('Points', 'desc'), Sort.by('FullName')))
          .filter((entry: LeaderboardV2Entry, index: number) => {
            if (this.isWidget) {
              return index < 3; // Show only top 3 entries for widget
            }
            return entry;
          });
        this.challengers = this.yearlyLeaderboards.slice(3, this.yearlyLeaderboards.length);
        this.topThree = this.getTopThreeTiers(this.yearlyLeaderboards);
        this.isUserTopThree = this.topThree.findIndex((entry: LeaderboardV2Entry) => entry.IsCurrentUser) !== -1;
      })
      .finally(() => (this.loadingYear = false));
  }

  async initializeMonthlyLeaderboard() {
    this.showMonthly = true;
    await firstValueFrom(this.leaderboardService.fetchMonthlyLeaderboardV2(this.toShow, this.LeaderboardType));
    this.monthly = this.leaderboardQuery.getMonthlyLeaderboardV2();

    // Initialize months array
    this.monthlyLeaderboard = this.monthly.Months.map((dateStr: string) => {
      const date = new Date(dateStr);
      return {
        month: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
        date: date,
        loaded: false,
        entries: [],
        topThree: [],
        challengers: [],
      };
    }).filter((monthData: any) => {
      return monthData.date <= new Date();
    });

    if (this.monthlyLeaderboard.length > 0) {
      await this.loadMonthData(this.monthlyLeaderboard.length - 1);
      this.reinitializeOwlCarousel();
    }
  }

  // Re-initialize the carousel because Safari is delaying rendering carousel
  reinitializeOwlCarousel(): void {
    if (this.owlCarouselRef) {
      this.owlCarouselRef.ngOnInit(); // Re-initializes the component
      this.owlCarouselRef.ngAfterContentInit(); // Ensures content is also re-processed
    }
  }

  // Load data for specific month
  async loadMonthData(index: number) {
    if (index < 0 || index >= this.monthlyLeaderboard.length) return;

    this.loadingMonth = index;
    if (!this.monthlyLeaderboard[index].loaded) {
      this.monthlyLeaderboard[index].loaded = false;
    }
    const dateStr = this.monthly.Months[index];
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    this.currentUser = undefined;
    await firstValueFrom(this.leaderboardService.fetchEachMonthLeaderboardV2(this.toShow, month, year, this.LeaderboardType))
      .then(() => {
        const monthData = this.leaderboardQuery
          .getEachMonthLeaderboardV2()
          .sort(sortMultiple(Sort.by('Position'), Sort.by('Points', 'desc'), Sort.by('FullName')));
        this.monthlyLeaderboard[index].entries = monthData.filter((entry: LeaderboardV2Entry, index: number) => {
          if (this.isWidget) {
            return index < 3; // Show only top 3 entries for widget
          }
          return entry;
        });
        this.monthlyLeaderboard[index].loaded = true;
        this.monthlyLeaderboard[index].topThree = this.getTopThreeTiers(this.monthlyLeaderboard[index].entries);
        this.monthlyLeaderboard[index].challengers = monthData.slice(3, this.monthlyLeaderboard[index].entries.length);
        this.isUserTopThree =
          this.monthlyLeaderboard[index].topThree.findIndex((entry: LeaderboardV2Entry) => entry.IsCurrentUser) !== -1;
        this.monthlyLeaderboard[index].currentUser = !this.isUserTopThree
          ? monthData.find((entry) => entry.IsCurrentUser)
          : undefined;
      })
      .finally(() => (this.monthlyLeaderboard[index].loaded = true));
  }

  // Carousel event handler
  async onSlideChanged(event: any) {
    const currentIndex = event.startPosition;
    await this.loadMonthData(currentIndex);
  }

  private getTopThreeTiers(rankings: LeaderboardV2Entry[]): LeaderboardEntryWithTier[] {
    let tiers = ['gold', 'silver', 'bronze'];
    let position: number = -1;
    return rankings.slice(0, 3).map((entry: LeaderboardV2Entry) => {
      if (entry.Position !== position && position !== -1) {
        tiers.shift();
      }

      position = entry.Position;
      return {
        ...entry,
        tier: tiers[0],
      };
    });
  }

  onTabChage(tab: number): void {
    if (tab === 0) {
      this.initializeMonthlyLeaderboard();
    } else if (tab === 1) {
      this.initializeLeaderboard();
    }
  }
}
